import { CalendarOff } from 'lucide-react';
import { dateKey, MONTHS_FR } from '../../utils/dateUtils';
import type { GridRow } from './types';
import type { DayStatuses } from '../../utils/sheetStatusUtils';

interface SkipMonthBannerProps {
  isStraddleWeek: boolean;
  activeEntryMonth: { year: number; month: number } | undefined;
  weekDays: Date[];
  rows: GridRow[];
  dayStatuses: DayStatuses;
  isLocked: boolean;
  isSaving: boolean;
  onSkipMonth: () => void;
}

/**
 * Bannière contextuelle affichée sur le 2ème mois d'une semaine à cheval
 * lorsque l'employé n'a aucune heure à saisir et qu'aucun jour de ce mois
 * n'a déjà été soumis ou validé.
 *
 * Action : convertir tous les jours du mois actif en "submitted" (sans
 * créer de time_entry) pour faire passer la feuille de "En cours" à
 * "À valider" — afin que le mois ne reste pas indéfiniment ouvert quand
 * l'employé sait qu'il n'aura aucune heure.
 *
 * Conditions d'affichage (toutes doivent être vraies) :
 *   - semaine à cheval (deux mois) ;
 *   - feuille non verrouillée (pas validée intégralement) ;
 *   - aucun jour du mois actif n'a `submitted` ou `validated` ;
 *   - aucune ligne avec heures sur des jours du mois actif.
 */
export function SkipMonthBanner({
  isStraddleWeek,
  activeEntryMonth,
  weekDays,
  rows,
  dayStatuses,
  isLocked,
  isSaving,
  onSkipMonth,
}: SkipMonthBannerProps) {
  // Pas de bannière si pas de cheval, mois inconnu, ou feuille verrouillée
  if (!isStraddleWeek || !activeEntryMonth || isLocked) return null;

  // Jours du mois actif dans cette semaine
  const activeMonthDays = weekDays.filter(
    d => d.getFullYear() === activeEntryMonth.year && d.getMonth() === activeEntryMonth.month,
  );
  if (activeMonthDays.length === 0) return null;

  // Si au moins un jour du mois actif est déjà soumis ou validé → ne pas afficher
  // (l'employé a déjà fait son choix de soumission pour ce mois).
  const hasAlreadyActionedDay = activeMonthDays.some(d => {
    const status = dayStatuses[dateKey(d)];
    return status === 'submitted' || status === 'validated';
  });
  if (hasAlreadyActionedDay) return null;

  // Si l'employé a saisi des heures sur le mois actif → pas de bannière
  // (il s'apprête à soumettre normalement).
  const activeMonthKeys = new Set(activeMonthDays.map(dateKey));
  const hasHoursInActiveMonth = rows.some(r =>
    Object.entries(r.hours).some(([ds, h]) => h > 0 && activeMonthKeys.has(ds)),
  );
  if (hasHoursInActiveMonth) return null;

  const monthLabel = MONTHS_FR[activeEntryMonth.month];

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
        border: '1px solid #7dd3fc',
        borderRadius: '16px',
        padding: '18px 22px',
        marginBottom: '24px',
        display: 'flex',
        alignItems: 'center',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <div
        style={{
          background: '#0ea5e9',
          color: 'white',
          padding: '12px',
          borderRadius: '12px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CalendarOff size={22} />
      </div>
      <div style={{ flex: 1, minWidth: 240 }}>
        <div style={{ fontWeight: 700, color: '#0c4a6e', fontSize: '1rem', marginBottom: '4px' }}>
          Pas d'heures à saisir en {monthLabel} {activeEntryMonth.year} ?
        </div>
        <div style={{ fontSize: '0.9rem', color: '#075985', lineHeight: 1.4 }}>
          Si vous ne travaillerez pas sur les jours de <strong>{monthLabel}</strong> de cette
          semaine, vous pouvez le confirmer pour clôturer la feuille. Aucune heure ne sera
          enregistrée — la feuille sortira de "En cours" et pourra être validée.
        </div>
      </div>
      <button
        onClick={onSkipMonth}
        disabled={isSaving}
        style={{
          background: isSaving ? '#94a3b8' : '#0ea5e9',
          color: 'white',
          border: 'none',
          padding: '12px 20px',
          borderRadius: '12px',
          fontWeight: 700,
          fontSize: '0.9rem',
          cursor: isSaving ? 'not-allowed' : 'pointer',
          boxShadow: '0 2px 4px rgba(14,165,233,0.3)',
          whiteSpace: 'nowrap',
        }}
      >
        {isSaving ? 'Enregistrement…' : `Confirmer : pas d'heures en ${monthLabel}`}
      </button>
    </div>
  );
}
